<template>
	<template v-if="hasSubmenu">
		<anchor-element tag="span" :chain="chain" class="tb-menu-item tb-submenu" @mousedown="mouseDown" @click="openMenu"><span class="tb-mi-segment"><sym-icon v-if="item.icon" :name="item.icon"></sym-icon><span v-bind="menuItemBind">{{ item.title }}</span></span><sym-icon name="caret-right-12" class="st-caret"></sym-icon></anchor-element>
		<boat-element :tag="ToolSubmenu" :items="item.children" class="tb-drop-menu" :chain="chain" :dir="direction" @action="submenuAction"></boat-element>
	</template>
	<span v-else class="tb-menu-item" @mousedown="mouseDown" @click="doMenuItemAction"><sym-icon v-if="item.icon" :name="item.icon"></sym-icon><span v-bind="menuItemBind">{{ item.title }}</span></span>
</template>

<script setup lang="ts">
import {ref, computed} from "vue";
import {ToolbarMenu, ToolbarMenuItem} from "@t/ui/Toolbar.ts";
import SymIcon from "@fd/helpers/SymIcon.vue";
import AnchorElement from "@/ui/paneltree/AnchorElement.vue";
import {createChain} from "@/ui/paneltree/PanelTree.ts";
import {ElementMeasure} from "@fd/helpers/DomUtils.ts";
import BoatElement from "@/ui/paneltree/BoatElement.vue";
import ToolSubmenu from "@/ui/toolbar/ToolSubmenu.vue";

const props = withDefaults(defineProps<{
	item: ToolbarMenuItem,
	dir: "left" | "right"
}>(), { dir: "right" });

const emit = defineEmits(['action']);

const hasSubmenu = computed<boolean>(() => Array.isArray(props.item.children));

const chain = hasSubmenu.value ? createChain({
	rule: (m:ElementMeasure) => {
		if ("right" === direction.value) {
			return {
				ah: 100, av: 50,
				bh: 0, bv: 50,
				x: -4, y: 0
			}
		} else {
			return {
				ah: 0, av: 50,
				bh: 100, bv: 50,
				x: 4, y: 0
			}
		}
	}
}, "tmi-"+props.item.id) : null;

const direction = computed<"left" | "right">(() => {
	if (chain?.anchor.value) {
		if ("left" === props.dir) {
			if (chain.anchor.value.spaceLeft < 100) return "right";
			return "left";
		} else {
			if (chain.anchor.value.spaceRight < 100) return "left";
			return "right";
		}
	}
	return props.dir;
});

function openMenu(e:MouseEvent) {
	e.preventDefault();
	chain.show();
}

function mouseDown(e:MouseEvent) {
	e.preventDefault();		// without preventing this click selection was lost
}

function submenuAction(id:string, arg?:any) {
	emit("action", id, arg);
}

function doMenuItemAction(e:MouseEvent) {
	emit("action", props.item.id, props.item.arg);
}

const menuItemBind = computed(() => {
	const ret:{ [key:string]:any } = {};
	if (props.item.style) ret.style = props.item.style;
	if (props.item.classes) ret.class = props.item.classes;
	return ret;
});

</script>

<style>
.tb-menu-item {
	background-color: var(--toolbar-button-bg);
	color: var(--toolbar-button-fg);
	padding: 2px 8px;
	display: flex;
	flex-direction: row;
	justify-content: flex-start;
	align-items: baseline;
	column-gap: 2px;

	&.tb-submenu {
		font-weight: bold;
		justify-content: space-between;
	}

	& .fi {
		min-width: 20px;

		&.st-caret {
			min-width: unset;
		}
	}

	&:not(.disabled):hover {
		background-color: var(--toolbar-button-bg-hover);
		color: var(--toolbar-button-fg-hover);
	}

	&:not(.disabled):active {
		background-color: var(--toolbar-button-bg-active);
		color: var(--toolbar-button-fg-active);
	}
}
.tb-mi-segment {
	display: flex;
	flex-direction: row;
	justify-content: flex-start;
	align-items: baseline;
	column-gap: 2px;
}
</style>